import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate } from "react-router-dom";
import "./Projects.css";
import { getApiUrl } from '../utils/api';

let projectsCache = null;

const Projects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [projects, setProjects] = useState(projectsCache || []);

  useEffect(() => {
    const url = getApiUrl('/api/projects');
    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log("Fetched projects data:", data);
        if (data && Array.isArray(data.data)) {
          const fallbackLinks = [
            "https://apps.apple.com/us/app/interior-design-ai/id6751872383",
            "https://apps.apple.com/us/app/aesthetica-ai/id6752387680",
            "https://apps.apple.com/in/app/durga-textile-agency/id6748637144",
            "https://apps.apple.com/in/app/preset-hub/id6745960035",
            "https://apps.apple.com/us/app/status-video-maker-pro/id6746097063"
          ];
          const projectsWithLinks = data.data.map((project, index) => ({
            ...project,
            appStoreLink: project.appStoreLink || fallbackLinks[index]
          }));
          projectsCache = projectsWithLinks;
          setProjects(projectsWithLinks);
        }
      })
      .catch(err => console.error("Error fetching projects:", err));
  }, []);

  useEffect(() => {
    if (projects.length > 0 && location.state?.scrollTo === 'projects') {
      if (location.state?.activeProjectId) {
        setTimeout(() => {
          const element = document.getElementById(`project-${location.state.activeProjectId}`);
          if (element) {
            if (window.lenis) {
              window.lenis.scrollTo(element, { offset: -80 });
            } else {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 100);
      } else {
        setTimeout(() => {
          const element = document.getElementById('projects');
          if (element) {
            if (window.lenis) {
              window.lenis.scrollTo(element);
            } else {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }, 50);
      }
    }
  }, [projects, location.state]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="projects" className="projects" ref={ref}>
      <motion.div
        className="projects-container"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.h2 className="projects-title" variants={itemVariants}>
          A brief glance into our work
        </motion.h2>

        <div className="projects-grid">
          {/* Row 1 */}
          {projects[0] && (
            <motion.div
              id={`project-${projects[0].id}`}
              className="services-section services-section-image-left"
              variants={itemVariants}
            >
              <div className="service-card">
                <div className="product-showcase">
                  <div className="product-image">
                    <img
                      src={projects[0].screenshotUrls?.[0] || "/images/interior_ai/home1.png"}
                      alt={projects[0].title}
                      className="product-image-content"
                    />
                  </div>
                </div>
                <div className="service-content">
                  <div className="service-item">
                    <h3>{projects[0].title}</h3>
                    <p className="clamp-8">{projects[0].description}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      className="view-project-btn"
                      onClick={() => navigate(`/project/${projects[0].id}`)}
                    >
                      VIEW PROJECT
                    </button>
                    {projects[0].appStoreLink && (
                      <a
                        href={projects[0].appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img
                          src="/images/apple.png"
                          alt="Download on App Store"
                          className="ios-logo"
                        />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Row 3 */}
          {projects[1] && (
            <motion.div
              id={`project-${projects[1].id}`}
              className="services-section-third"
              variants={itemVariants}
            >
              <div className="service-card">
                <div className="service-content">
                  <div className="service-item">
                    <h3>{projects[1].title}</h3>
                    <p className="clamp-8">{projects[1].description}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      className="view-project-btn"
                      onClick={() => navigate(`/project/${projects[1].id}`)}
                    >
                      VIEW PROJECT
                    </button>
                    {projects[1].appStoreLink && (
                      <a
                        href={projects[1].appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img
                          src="/images/apple.png"
                          alt="Download on App Store"
                          className="ios-logo"
                        />
                      </a>
                    )}
                  </div>
                </div>
                <div className="product-showcase">
                  <div className="product-image">
                    <img
                      src={projects[1].screenshotUrls?.[0] || "/images/asthica/home.png"}
                      alt={projects[1].title}
                      className="product-image-content"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Row 4 */}
          {projects[2] && (
            <motion.div
              id={`project-${projects[2].id}`}
              className="bottom-service-card-fourth"
              variants={itemVariants}
            >
              <div className="service-card">
                <div className="product-showcase">
                  <div className="product-image">
                    <img
                      src={projects[2].screenshotUrls?.[0] || "/images/dta/home.png"}
                      alt={projects[2].title}
                      className="product-image-content"
                    />
                  </div>
                </div>
                <div className="service-content">
                  <div className="service-item">
                    <h3>{projects[2].title}</h3>
                    <p className="clamp-8">{projects[2].description}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      className="view-project-btn"
                      onClick={() => navigate(`/project/${projects[2].id}`)}
                    >
                      VIEW PROJECT
                    </button>
                    {projects[2].appStoreLink && (
                      <a
                        href={projects[2].appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img
                          src="/images/apple.png"
                          alt="Download on App Store"
                          className="ios-logo"
                        />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Row 5 */}
          {projects[3] && (
            <motion.div
              id={`project-${projects[3].id}`}
              className="services-section-fifth"
              variants={itemVariants}
            >
              <div className="service-card">
                <div className="service-content">
                  <div className="service-item">
                    <h3>{projects[3].title}</h3>
                    <p>{projects[3].description}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      className="view-project-btn"
                      onClick={() => navigate(`/project/${projects[3].id}`)}
                    >
                      VIEW PROJECT
                    </button>
                    {projects[3].appStoreLink && (
                      <a
                        href={projects[3].appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img
                          src="/images/apple.png"
                          alt="Download on App Store"
                          className="ios-logo"
                        />
                      </a>
                    )}
                  </div>
                </div>
                <div className="product-showcase">
                  <div className="product-image">
                    <img
                      src={projects[3].screenshotUrls?.[0] || "/images/preset_hub/home.png"}
                      alt={projects[3].title}
                      className="product-image-content"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Row 6 */}
          {projects[4] && (
            <motion.div
              id={`project-${projects[4].id}`}
              className="bottom-service-card-sixth"
              variants={itemVariants}
            >
              <div className="service-card">
                <div className="product-showcase">
                  <div className="product-image">
                    <img
                      src={projects[4].screenshotUrls?.[0] || "/images/status_video/statushome.png"}
                      alt={projects[4].title}
                      className="product-image-content"
                    />
                  </div>
                </div>
                <div className="service-content">
                  <div className="service-item">
                    <h3>{projects[4].title}</h3>
                    <p>{projects[4].description}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      className="view-project-btn"
                      onClick={() => navigate(`/project/${projects[4].id}`)}
                    >
                      VIEW PROJECT
                    </button>
                    {projects[4].appStoreLink && (
                      <a
                        href={projects[4].appStoreLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="app-store-link"
                      >
                        <img
                          src="/images/apple.png"
                          alt="Download on App Store"
                          className="ios-logo"
                        />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  );
};

export default Projects;
